import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Building2,
  Wrench,
  Laptop,
  ShoppingCart,
  Calendar,
  Edit,
  UserPlus,
  KeyRound,
  Wallet,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [creatingAccount, setCreatingAccount] = useState(false);

  const fetchClient = async () => {
    try {
      const response = await API.get(`/clients/${id}`);
      setClient(response.data);
    } catch (error) {
      toast.error("Client non trouvé");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
  }, [id, navigate]);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!accountPassword || accountPassword.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    setCreatingAccount(true);
    try {
      await API.post(`/clients/${id}/create-account`, { password: accountPassword });
      toast.success("Compte client créé avec succès");
      setAccountDialogOpen(false);
      setAccountPassword("");
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création du compte");
    } finally {
      setCreatingAccount(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!client) return null;

  const statusColors = {
    appareil_depose: "bg-blue-100 text-blue-800",
    diagnostic_en_cours: "bg-yellow-100 text-yellow-800",
    devis_envoye: "bg-purple-100 text-purple-800",
    reparation_en_cours: "bg-orange-100 text-orange-800",
    pret_a_restituer: "bg-green-100 text-green-800",
    cloture: "bg-gray-100 text-gray-800"
  };

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/clients")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">{client.prenom} {client.nom}</h1>
          {client.societe && (
            <p className="text-muted-foreground flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {client.societe}
            </p>
          )}
        </div>
        <Button variant="outline" onClick={() => navigate(`/clients`)}>
          <Edit className="h-4 w-4 mr-2" />
          Modifier
        </Button>
        {client.email && !client.user_id && (
          <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-client-account-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Créer un accès client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleCreateAccount}>
                <DialogHeader>
                  <DialogTitle>Créer un accès client</DialogTitle>
                  <DialogDescription>
                    Créez un compte pour que {client.prenom} {client.nom} puisse suivre ses dossiers SAV en ligne.
                    L'email utilisé sera : <strong>{client.email}</strong>
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label>Mot de passe</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Mot de passe temporaire"
                        className="pl-10"
                        value={accountPassword}
                        onChange={(e) => setAccountPassword(e.target.value)}
                        required
                        minLength={6}
                        data-testid="client-account-password-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Communiquez ce mot de passe au client. Il pourra le changer ultérieurement.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setAccountDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={creatingAccount} data-testid="confirm-create-account-btn">
                    {creatingAccount ? "Création..." : "Créer le compte"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        {client.user_id && (
          <Badge variant="secondary" className="h-9 px-3 gap-2">
            <UserPlus className="h-4 w-4" />
            Accès client actif
          </Badge>
        )}
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                <Phone className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Téléphone</p>
                <p className="font-medium">{client.telephone}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                <Mail className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{client.email || "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Adresse</p>
                <p className="font-medium text-sm">{client.adresse || "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={(client.solde_credit || 0) > 0 ? "border-emerald-200 bg-emerald-50/30" : ""}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${(client.solde_credit || 0) > 0 ? "bg-emerald-100" : "bg-gray-100"}`}>
                <Wallet className={`h-5 w-5 ${(client.solde_credit || 0) > 0 ? "text-emerald-600" : "text-gray-400"}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Crédit boutique</p>
                <p className={`font-bold text-lg ${(client.solde_credit || 0) > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                  {(client.solde_credit || 0).toFixed(2)} EUR
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="sav" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sav" className="gap-2">
            <Wrench className="h-4 w-4" />
            Dossiers SAV ({client.sav_cases?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="devices" className="gap-2">
            <Laptop className="h-4 w-4" />
            Appareils ({client.devices?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="sales" className="gap-2">
            <ShoppingCart className="h-4 w-4" />
            Achats ({client.sales?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sav">
          <Card>
            <CardHeader>
              <CardTitle>Historique SAV</CardTitle>
              <CardDescription>Tous les dossiers SAV du client</CardDescription>
            </CardHeader>
            <CardContent>
              {client.sav_cases?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Numéro</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {client.sav_cases.map((sav) => (
                      <TableRow key={sav.id}>
                        <TableCell className="font-mono">{sav.numero}</TableCell>
                        <TableCell>
                          {new Date(sav.created_at).toLocaleDateString("fr-FR")}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[sav.statut] || "bg-gray-100"}>
                            {sav.statut?.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={sav.type_prise_en_charge === "garantie" ? "default" : "secondary"}>
                            {sav.type_prise_en_charge}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/sav/${sav.id}`)}
                          >
                            Voir
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucun dossier SAV</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices">
          <Card>
            <CardHeader>
              <CardTitle>Appareils enregistrés</CardTitle>
              <CardDescription>Tous les appareils du client</CardDescription>
            </CardHeader>
            <CardContent>
              {client.devices?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Appareil</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>N° Série / IMEI</TableHead>
                      <TableHead>Date dépôt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {client.devices.map((device) => (
                      <TableRow key={device.id}>
                        <TableCell className="font-medium">
                          {device.marque} {device.modele}
                        </TableCell>
                        <TableCell className="capitalize">
                          {device.categorie?.replace(/_/g, " ")}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {device.numero_serie || device.imei || "-"}
                        </TableCell>
                        <TableCell>
                          {new Date(device.date_depot).toLocaleDateString("fr-FR")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucun appareil enregistré</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sales">
          <Card>
            <CardHeader>
              <CardTitle>Historique d'achats</CardTitle>
              <CardDescription>Toutes les ventes associées au client</CardDescription>
            </CardHeader>
            <CardContent>
              {client.sales?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Numéro</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Articles</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {client.sales.map((sale) => (
                      <TableRow key={sale.id}>
                        <TableCell className="font-mono">{sale.numero}</TableCell>
                        <TableCell>
                          {new Date(sale.created_at).toLocaleDateString("fr-FR")}
                        </TableCell>
                        <TableCell>{sale.articles?.length || 0} article(s)</TableCell>
                        <TableCell className="text-right font-medium">
                          {sale.total_ttc?.toFixed(2)}€
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucun achat</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Notes */}
      {client.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes internes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{client.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientDetailPage;
